import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material'
import Search from '@mui/icons-material/Search'
import Close from '@mui/icons-material/Close'
import ArrowForward from '@mui/icons-material/ArrowForward'
import LocalHospital from '@mui/icons-material/LocalHospital'
import LocationOn from '@mui/icons-material/LocationOn'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import { HighlightedText } from './clinicSearch'

const normalize = (value) => (value == null ? '' : String(value)).toLowerCase().trim()
const MAX_RESULTS = 14

// Flattens the brand → location → room tree into a ranked, de-duplicated list of matches for the
// query (brands first, then locations, then rooms). Each result carries the brand+location ids
// needed to navigate, plus a breadcrumb sublabel for context.
function buildSearchResults(brands = [], rawQuery = '') {
  const query = normalize(rawQuery)
  if (!query) return []

  const results = []
  brands.forEach((brand) => {
    const brandId = brand?.id
    const locations = brand?.locations || []
    const firstLocation = locations.find((location) => location?.id)

    if (brandId && firstLocation && normalize(brand?.title).includes(query)) {
      results.push({
        key: `brand-${brandId}`,
        type: 'brand',
        label: brand?.title || 'Clinic',
        sublabel: `${locations.length} location${locations.length !== 1 ? 's' : ''}`,
        brandId,
        locationId: firstLocation.id,
      })
    }

    locations.forEach((location) => {
      const locationId = location?.id
      if (!locationId) return

      if (normalize(location?.name).includes(query)) {
        results.push({
          key: `location-${brandId}-${locationId}`,
          type: 'location',
          label: location?.name || 'Location',
          sublabel: brand?.title || '',
          brandId,
          locationId,
        })
      }

      ;(location?.rooms || []).forEach((room) => {
        if (normalize(room?.title).includes(query)) {
          results.push({
            key: `room-${room?.id || `${locationId}-${room?.title}`}`,
            type: 'room',
            label: room?.title || 'Room',
            sublabel: `${location?.name || ''}${brand?.title ? ` · ${brand.title}` : ''}`,
            brandId,
            locationId,
          })
        }
      })
    })
  })

  return results.slice(0, MAX_RESULTS)
}

const TYPE_META = {
  brand: { icon: LocalHospital, label: 'Clinic', action: 'Open' },
  location: { icon: LocationOn, label: 'Location', action: 'Open' },
  room: { icon: MeetingRoom, label: 'Room', action: 'Join' },
}

/**
 * Search-as-you-type launcher for the clinic portal. Shows a structured dropdown of matching
 * brands / locations / rooms, each with a context breadcrumb and an action button that opens the
 * relevant clinic location (where the practitioner joins / checks in).
 */
export default function ClinicSearchDropdown({ brands = [] }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  const results = useMemo(() => buildSearchResults(brands, query), [brands, query])
  const showDropdown = open && query.trim().length > 0

  const goToResult = (result) => {
    if (!result?.brandId || !result?.locationId) return
    navigate(`/mentor-home/my-clinic-associations/${result.brandId}/${result.locationId}`)
    setOpen(false)
    setQuery('')
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: { xs: '100%', sm: 260 } }}>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search clinics, locations, rooms"
          aria-label="Search clinics, locations, rooms"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'rgba(100, 116, 139, 0.7)' }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery('')
                    setOpen(false)
                  }}
                  sx={{ color: 'rgba(100, 116, 139, 0.8)' }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              backgroundColor: 'rgba(248, 250, 252, 0.9)',
              fontSize: '13px',
            },
          }}
        />

        <Popper
          open={showDropdown}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper
            sx={{
              mt: 0.75,
              maxHeight: 360,
              overflowY: 'auto',
              borderRadius: '14px',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 16px 36px rgba(15, 23, 42, 0.18)',
            }}
          >
            {results.length === 0 ? (
              <Box sx={{ px: 2, py: 1.75 }}>
                <Typography sx={{ fontSize: '13px', color: 'rgba(100, 116, 139, 0.9)' }}>
                  No clinics, locations or rooms match &ldquo;{query.trim()}&rdquo;
                </Typography>
              </Box>
            ) : (
              results.map((result) => {
                const meta = TYPE_META[result.type]
                const Icon = meta.icon
                return (
                  <Box
                    key={result.key}
                    role="button"
                    tabIndex={0}
                    onClick={() => goToResult(result)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        goToResult(result)
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(15, 23, 42, 0.05)',
                      '&:last-of-type': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: 'rgba(116, 80, 170, 0.07)' },
                      '&:focus-visible': {
                        outline: '2px solid rgba(116, 80, 170, 0.5)',
                        outlineOffset: '-2px',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '8px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(116, 80, 170, 0.1)',
                        color: 'rgba(116, 80, 170, 0.9)',
                      }}
                    >
                      <Icon sx={{ fontSize: 17 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '13.5px',
                          fontWeight: 600,
                          color: 'rgba(15, 23, 42, 0.92)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <HighlightedText text={result.label} query={query} />
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '11.5px',
                          color: 'rgba(100, 116, 139, 0.9)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {meta.label}
                        {result.sublabel ? ` · ${result.sublabel}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="emrSecondary"
                      endIcon={<ArrowForward sx={{ fontSize: '15px !important' }} />}
                      onClick={(event) => {
                        event.stopPropagation()
                        goToResult(result)
                      }}
                      sx={{
                        flexShrink: 0,
                        borderRadius: '999px',
                        textTransform: 'none',
                        minHeight: 30,
                        px: 1.5,
                        fontSize: '12.5px',
                      }}
                    >
                      {meta.action}
                    </Button>
                  </Box>
                )
              })
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
